import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errMsg, priceLabel, STATUS_LABEL, STATUS_COLOR } from "@/lib/api";
import { useServiceMap } from "@/lib/data";

const FILTERS = [
  ["", "Todos", "requests_total"], ["aberto", "Em aberto", "requests_open"], ["aceito", "Aceitos", "requests_accepted"],
  ["concluido", "Concluídos", "requests_done"], ["cancelado", "Cancelados", "requests_canceled"], ["sem_prestador", "Sem prestador", "requests_no_provider"],
];
const fmt = (d) => new Date(d).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
const CH = { push: "Push", email: "E-mail", whatsapp: "WhatsApp" };
const ST = {
  sent: ["enviado", "bg-green-100 text-green-800"],
  failed: ["falhou", "bg-red-100 text-red-700"],
  no_subscription: ["sem notificação ativa", "bg-yellow-100 text-yellow-800"],
  not_configured: ["não configurado", "bg-slate-200 text-slate-700"],
  invalid_number: ["sem WhatsApp válido", "bg-yellow-100 text-yellow-800"],
  skipped: ["não enviado (teste)", "bg-slate-200 text-slate-700"],
};

function WhatsAppTest() {
  const [num, setNum] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { setRes((await api.post("/admin/whatsapp/test", { numero: num })).data); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={send} className="po-card mb-5 flex flex-col gap-3 sm:flex-row sm:items-end" data-testid="admin-whatsapp-test">
      <div className="flex-1">
        <label className="po-label" htmlFor="wa-test">Testar aviso por WhatsApp</label>
        <input id="wa-test" className="po-input" type="tel" required minLength={8} value={num} onChange={(e) => setNum(e.target.value)} placeholder="(62) 99999-9999" data-testid="admin-whatsapp-test-input" />
      </div>
      <button disabled={busy} className="po-btn-dark" data-testid="admin-whatsapp-test-button">{busy ? "Enviando..." : "Enviar WhatsApp de teste"}</button>
      {res && (
        <p className={`rounded-full px-3 py-2 text-sm font-bold ${ST[res.status]?.[1] || "bg-slate-100"}`} data-testid="admin-whatsapp-test-result">
          {res.status === "not_configured" ? res.detail : `${ST[res.status]?.[0] || res.status}${res.detail ? ` · ${res.detail}` : ""}`}
        </p>
      )}
    </form>
  );
}

function NotifyLog({ r }) {
  const log = r.notify_log || [];
  if (!log.length) return <span className="text-sm text-slate-500">—</span>;
  return (
    <div className="space-y-1" data-testid={`admin-request-notify-${r.id}`}>
      {log.slice(-6).map((n, i) => (
        <p key={i} className="text-sm" title={n.detail || ""}>
          <span className="font-semibold">{CH[n.channel] || n.channel}</span>{n.provider_name ? ` → ${n.provider_name}` : ""}:{" "}
          <span className={`rounded-full px-2 py-0.5 font-bold ${ST[n.status]?.[1] || "bg-slate-100"}`}>{ST[n.status]?.[0] || n.status}</span>
          {n.status === "failed" && n.detail && <span className="block text-xs text-red-700">{n.detail}</span>}
        </p>
      ))}
    </div>
  );
}

export default function AdminRequests({ filter: status = "", setFilter: setStatus, counts }) {
  const qc = useQueryClient();
  const services = useServiceMap();
  const { data = [], isFetching } = useQuery({
    queryKey: ["admin-requests", status],
    queryFn: () => api.get("/admin/requests", { params: status ? { status } : {} }).then((r) => r.data),
    refetchInterval: 10000,
  });
  const cancel = async (id) => {
    if (!window.confirm("Cancelar este pedido?")) return;
    try { await api.post(`/admin/requests/${id}/cancel`); qc.invalidateQueries({ queryKey: ["admin-requests"] }); qc.invalidateQueries({ queryKey: ["admin-overview"] }); } catch (e) { toast.error(errMsg(e)); }
  };
  const current = FILTERS.find(([v]) => v === status) || FILTERS[0];
  const svcName = (r) => services[r.service_slug]?.name || r.service_slug;
  const canCancel = (r) => ["buscando", "aceito", "sem_prestador"].includes(r.status);
  return (
    <div>
      <WhatsAppTest />
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([v, l, k]) => (
          <button key={v} onClick={() => setStatus(v)} data-testid={`admin-request-filter-${v || "all"}`} aria-pressed={status === v}
            className={`min-h-[44px] rounded-full border-2 px-4 text-base font-bold ${status === v ? "border-orange-500 bg-orange-500 text-white" : "border-slate-300 text-slate-700"}`}>
            {l} ({counts?.[k] ?? "–"})
          </button>
        ))}
      </div>
      <p className="mt-4 text-lg font-semibold text-slate-700" data-testid="admin-requests-showing">Mostrando: {current[1]} · {isFetching && !data.length ? "..." : data.length} pedido(s)</p>
      <div className="mt-3 space-y-3 md:hidden" data-testid="admin-requests-cards">
        {data.map((r) => (
          <div key={r.id} className="po-card" data-testid={`admin-request-card-${r.id}`}>
            <div className="flex items-start justify-between gap-2">
              <p className="text-lg font-bold text-slate-900">{svcName(r)}{r.is_test && <span className="ml-1 rounded bg-slate-200 px-1.5 text-xs">teste</span>}</p>
              <span className={`shrink-0 rounded-full px-3 py-1 text-sm font-bold ${STATUS_COLOR[r.status]}`}>{STATUS_LABEL[r.status]}</span>
            </div>
            <p className="text-base text-slate-700">Cliente: {r.client_name}</p>
            <p className="text-base text-slate-700">{r.neighborhood}, {r.city}</p>
            <p className="text-base text-slate-700">{priceLabel(r)} · {fmt(r.created_at)}</p>
            <p className="text-base text-slate-700">Prestador: {r.provider_name || `— (${r.offered_count} consultados)`}</p>
            <div className="mt-2"><NotifyLog r={r} /></div>
            <div className="mt-3 flex gap-4">
              <Link to={`/pedido/${r.id}`} className="font-semibold text-orange-600 underline">Ver pedido</Link>
              {canCancel(r) && <button onClick={() => cancel(r.id)} className="font-semibold text-red-600 underline">Cancelar</button>}
            </div>
          </div>
        ))}
        {!isFetching && data.length === 0 && <p className="text-lg text-slate-600">Nenhum pedido aqui</p>}
      </div>
      <div className="mt-3 hidden overflow-x-auto rounded-3xl border border-slate-200 md:block">
        <table className="w-full min-w-[980px] text-left text-base" data-testid="admin-requests-table">
          <thead className="bg-slate-50 text-slate-600">
            <tr><th className="p-3">Data e hora</th><th className="p-3">Serviço</th><th className="p-3">Valor</th><th className="p-3">Local</th><th className="p-3">Cliente</th><th className="p-3">Prestador</th><th className="p-3">Status</th><th className="p-3">Avisos ao prestador</th><th className="p-3" /></tr>
          </thead>
          <tbody>
            {data.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`admin-request-${r.id}`}>
                <td className="p-3 whitespace-nowrap">{fmt(r.created_at)}</td>
                <td className="p-3">{svcName(r)}{r.is_test && <span className="ml-1 rounded bg-slate-200 px-1.5 text-xs">teste</span>}</td>
                <td className="p-3 whitespace-nowrap" data-testid={`admin-request-price-${r.id}`}>{priceLabel(r)}</td>
                <td className="p-3">{r.neighborhood}, {r.city}</td>
                <td className="p-3">{r.client_name}</td>
                <td className="p-3">{r.provider_name || `— (${r.offered_count} consultados)`}</td>
                <td className="p-3"><span className={`rounded-full px-3 py-1 text-sm font-bold ${STATUS_COLOR[r.status]}`}>{STATUS_LABEL[r.status]}</span></td>
                <td className="p-3 min-w-[220px]"><NotifyLog r={r} /></td>
                <td className="p-3 whitespace-nowrap">
                  <Link to={`/pedido/${r.id}`} className="font-semibold text-orange-600 underline">Ver</Link>
                  {canCancel(r) && <button onClick={() => cancel(r.id)} className="ml-3 font-semibold text-red-600 underline" data-testid={`admin-request-cancel-${r.id}`}>Cancelar</button>}
                </td>
              </tr>
            ))}
            {!isFetching && data.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-slate-600" data-testid="admin-requests-empty">Nenhum pedido aqui</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
